import { MOVEMENT_RADIUS, metersToPixels } from '../sim/constants.ts';
import {
  AIM_BIAS_MAX,
  type PlayCameraLimit,
  type PlayCameraSettings,
} from '../sim/debugDefaults/camera.ts';
import { clamp } from '../sim/math.ts';
import type { MovementArea, Vec2 } from '../sim/types.ts';
import { MAX_ZOOM, PLAY_MIN_ZOOM, WHEEL_ZOOM_IN, WHEEL_ZOOM_OUT } from './view.ts';

export {
  AIM_BIAS_MAX,
  AUTO_CAMERA_HINT,
  CAMERA_READOUT_HINTS,
  DEFAULT_PLAY_CAMERA,
  PLAY_CAMERA_SLIDERS,
  formatPlayCameraLimit,
  formatUserZoomFactor,
  formatZoom,
  patchPlayCamera,
} from '../sim/debugDefaults/camera.ts';
export type {
  PlayCameraLimit,
  PlayCameraSettings,
  PlayCameraSliderKey,
  PlayCameraSliderSpec,
} from '../sim/debugDefaults/camera.ts';

/**
 * Play-camera policy: target zoom and look-ahead from boat + movement circle.
 * GameScene applies the numbers to Phaser; this module does not import it.
 *
 * Feel work and the remaining tuning log live in docs/PLAY-CAMERA.md.
 */

/** Dest chrome can die while the camera still leans at the last aim. */
export type AimReleaseMode = 'fade' | 'hold';

export type CameraAimState = {
  point: Vec2 | null;
  weight: number;
};

export function emptyCameraAim(): CameraAimState {
  return { point: null, weight: 0 };
}

/** Speed that maps to `fastZoom`; one max-radius move. */
export const PLAY_CAMERA_REF_SPEED = MOVEMENT_RADIUS;

/** Playwright / design viewport. Speed-curve sliders are authored at this size. */
export const PLAY_CAMERA_REF_VIEWPORT = { width: 1280, height: 720 } as const;

export { WHEEL_ZOOM_IN, WHEEL_ZOOM_OUT } from './view.ts';

const LIMIT_EPS = 1e-4;

export type PlayCameraArgs = {
  hull: Vec2;
  area: MovementArea;
  speed: number;
  viewportWidth: number;
  viewportHeight: number;
  settings: PlayCameraSettings;
  userZoomFactor: number;
  /** Camera aim point. Pan-only pull; ignored when null. May lag the hover dest. */
  aim?: Vec2 | null;
  /** 0–1 scale on `aimBias`. Fade home decays this; dest present is 1. */
  aimWeight?: number;
  /** 0–1 scale on speed look-ahead toward the circle. Default 1 (full). */
  lookAheadGate?: number;
  /** Ghost-hull placement, before plant. Uses `placeZoom` instead of `restZoom`. */
  placing?: boolean;
};

export type PlayCameraResult = {
  lookAhead: Vec2;
  followOffset: Vec2;
  fitZoom: number;
  speedZoom: number;
  autoZoom: number;
  appliedZoom: number;
  limit: PlayCameraLimit;
};

export function lookAheadPoint(
  hull: Vec2,
  areaCenter: Vec2,
  lookAhead: number,
): Vec2 {
  const t = clamp(lookAhead, 0, AIM_BIAS_MAX);

  return {
    x: hull.x + (areaCenter.x - hull.x) * t,
    y: hull.y + (areaCenter.y - hull.y) * t,
  };
}

/** Look-ahead slider is the value at full speed; rest is always 0. */
export function lookAheadAmount(
  speed: number,
  lookAhead: number,
  refSpeed: number = PLAY_CAMERA_REF_SPEED,
): number {
  const speedT = refSpeed <= 0 ? 0 : clamp(speed / refSpeed, 0, 1);

  return clamp(lookAhead, 0, 1) * speedT;
}

/**
 * 0–1 scale on speed look-ahead toward the planning circle.
 * Racing / replay want 1 so the Look-ahead slider always applies; handoff
 * zeros it so a seat switch does not yank toward the new circle mid-glide.
 */
export function stepLookAheadGate(
  gate: number,
  want: number,
  smoothness: number,
  dt: number,
): number {
  return clamp(smoothToward(gate, clamp(want, 0, 1), smoothness, dt), 0, 1);
}

/**
 * Phaser centres on `follow.x - followOffset.x`. Following the hull and wanting
 * the view on `target` means the offset is `hull - target`.
 */
export function followOffsetFromHull(hull: Vec2, target: Vec2): Vec2 {
  return { x: hull.x - target.x, y: hull.y - target.y };
}

export function viewportZoomScale(
  viewportWidth: number,
  viewportHeight: number,
): number {
  if (viewportWidth <= 0 || viewportHeight <= 0) {
    return 1;
  }

  return Math.min(
    viewportWidth / PLAY_CAMERA_REF_VIEWPORT.width,
    viewportHeight / PLAY_CAMERA_REF_VIEWPORT.height,
  );
}

export function speedZoom(
  speed: number,
  restZoom: number,
  fastZoom: number,
  refSpeed: number = PLAY_CAMERA_REF_SPEED,
  sensitivity: number = 1,
): number {
  const t = refSpeed <= 0 ? 0 : clamp(speed / refSpeed, 0, 1);
  const pull = Math.max(0, sensitivity);

  return restZoom + (fastZoom - restZoom) * t * pull;
}

export function blendFit(
  fromSpeed: number,
  fit: number,
  fitBlend: number,
): number {
  const tighter = Math.min(fit, fromSpeed);

  return fromSpeed + (tighter - fromSpeed) * clamp(fitBlend, 0, 1);
}

/**
 * Fit zoom from the hull + framed-disk AABB size. Look-ahead is a pan and is
 * not an input — Fit is a bias, not a guarantee the far rim stays on screen.
 */
export function fitZoom(
  hull: Vec2,
  area: MovementArea,
  viewportWidth: number,
  viewportHeight: number,
  paddingM: number,
  circleFit: number = 1,
): number {
  const pad = metersToPixels(Math.max(0, paddingM));
  const keep = area.radius * clamp(circleFit, 0, 1);
  const minX = Math.min(hull.x, area.center.x - keep) - pad;
  const maxX = Math.max(hull.x, area.center.x + keep) + pad;
  const minY = Math.min(hull.y, area.center.y - keep) - pad;
  const maxY = Math.max(hull.y, area.center.y + keep) + pad;
  const neededW = Math.max(maxX - minX, 1);
  const neededH = Math.max(maxY - minY, 1);

  if (viewportWidth <= 0 || viewportHeight <= 0) {
    return PLAY_MIN_ZOOM;
  }

  return Math.min(viewportWidth / neededW, viewportHeight / neededH);
}

/**
 * Fit zoom and centre for an arbitrary set of world points (fleet hulls, look-ahead
 * marks). Size-only: the AABB plus `paddingM` on every side. `null` when no finite
 * point is supplied.
 */
export function fitPointsFrame(
  points: readonly Vec2[],
  viewportWidth: number,
  viewportHeight: number,
  paddingM: number,
): { zoom: number; center: Vec2 } | null {
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;

  for (const point of points) {
    if (!Number.isFinite(point.x) || !Number.isFinite(point.y)) {
      continue;
    }

    minX = Math.min(minX, point.x);
    minY = Math.min(minY, point.y);
    maxX = Math.max(maxX, point.x);
    maxY = Math.max(maxY, point.y);
  }

  if (!Number.isFinite(minX) || !Number.isFinite(minY)) {
    return null;
  }

  const pad = metersToPixels(Math.max(0, paddingM));
  const boxW = Math.max(1, maxX - minX + pad * 2);
  const boxH = Math.max(1, maxY - minY + pad * 2);
  const zoom =
    viewportWidth <= 0 || viewportHeight <= 0
      ? PLAY_MIN_ZOOM
      : Math.min(viewportWidth / boxW, viewportHeight / boxH);

  return {
    zoom: clamp(zoom, PLAY_MIN_ZOOM, MAX_ZOOM),
    center: { x: (minX + maxX) / 2, y: (minY + maxY) / 2 },
  };
}

/** Place / planted-rest: the Zoom readout is the slider, not Fit or the viewport metre scale. */
function pinRestAutoZoom(
  rest: number,
  settings: PlayCameraSettings,
  viewportScale: number,
): { autoZoom: number; limit: PlayCameraLimit } {
  const lo =
    Math.max(
      PLAY_MIN_ZOOM,
      Math.min(settings.minAutoZoom, settings.maxAutoZoom) * viewportScale,
    );
  const hi =
    Math.min(
      MAX_ZOOM,
      Math.max(settings.minAutoZoom, settings.maxAutoZoom) * viewportScale,
    );
  const auto = clamp(rest, lo, hi);

  if (rest < lo - LIMIT_EPS) {
    return { autoZoom: auto, limit: 'Min' };
  }

  if (rest > hi + LIMIT_EPS) {
    return { autoZoom: auto, limit: 'Max' };
  }

  return { autoZoom: auto, limit: 'Speed' };
}

export function resolveAutoZoom(
  fit: number,
  fromSpeed: number,
  settings: PlayCameraSettings,
  viewportScale: number,
): { autoZoom: number; limit: PlayCameraLimit } {
  const blended = blendFit(fromSpeed, fit, settings.fitBlend);
  const lo =
    Math.min(settings.minAutoZoom, settings.maxAutoZoom) * viewportScale;
  const hi =
    Math.max(settings.minAutoZoom, settings.maxAutoZoom) * viewportScale;
  const loClamped = Math.max(PLAY_MIN_ZOOM, lo);
  const hiClamped = Math.min(MAX_ZOOM, hi);
  const auto = clamp(blended, loClamped, hiClamped);

  if (blended < loClamped - LIMIT_EPS) {
    return { autoZoom: auto, limit: 'Min' };
  }

  if (blended > hiClamped + LIMIT_EPS) {
    return { autoZoom: auto, limit: 'Max' };
  }

  if (Math.abs(blended - fromSpeed) > LIMIT_EPS) {
    return { autoZoom: auto, limit: 'Fit' };
  }

  return { autoZoom: auto, limit: 'Speed' };
}

export function autoZoom(
  fit: number,
  speed: number,
  settings: PlayCameraSettings,
  viewportScale: number = 1,
): number {
  const fromSpeed = speedZoom(
    speed,
    settings.restZoom,
    settings.fastZoom,
    PLAY_CAMERA_REF_SPEED,
    settings.speedSensitivity,
  );

  if (speed <= 0) {
    return pinRestAutoZoom(fromSpeed, settings, viewportScale).autoZoom;
  }

  return resolveAutoZoom(fit, fromSpeed, settings, viewportScale).autoZoom;
}

export function appliedZoom(auto: number, userZoomFactor: number): number {
  return clamp(auto * userZoomFactor, PLAY_MIN_ZOOM, MAX_ZOOM);
}

export function nextUserZoomFactor(
  userZoomFactor: number,
  auto: number,
  zoomOut: boolean,
): number {
  const stepped = userZoomFactor * (zoomOut ? WHEEL_ZOOM_OUT : WHEEL_ZOOM_IN);
  const next = appliedZoom(auto, stepped);

  return auto > 0 ? next / auto : stepped;
}

export function userFactorForZoom(auto: number, zoom: number): number {
  if (auto <= 0) {
    return 1;
  }

  return clamp(zoom, PLAY_MIN_ZOOM, MAX_ZOOM) / auto;
}

export function smoothToward(
  current: number,
  target: number,
  smoothness: number,
  dt: number,
): number {
  if (smoothness <= 0 || dt <= 0) {
    return target;
  }

  return current + (target - current) * (1 - Math.exp(-smoothness * dt));
}

const AIM_WEIGHT_EPS = 1e-3;

/**
 * Camera aim is not the hover dest. Dest present eases weight up and pins the point.
 * Dest gone: hold keeps the last lean; fade decays weight toward look-ahead.
 */
export function stepCameraAim(
  state: CameraAimState,
  dest: Vec2 | null,
  mode: AimReleaseMode,
  smoothness: number,
  dt: number,
): CameraAimState {
  if (dest) {
    const weight = smoothToward(state.weight, 1, smoothness, dt);

    return {
      point: { x: dest.x, y: dest.y },
      weight: weight < AIM_WEIGHT_EPS ? AIM_WEIGHT_EPS : weight,
    };
  }

  if (!state.point || state.weight <= 0) {
    return { point: null, weight: 0 };
  }

  if (mode === 'hold') {
    return { point: state.point, weight: 1 };
  }

  const weight = smoothToward(state.weight, 0, smoothness, dt);

  if (weight < AIM_WEIGHT_EPS) {
    return { point: null, weight: 0 };
  }

  return { point: state.point, weight };
}

/** Zoom-in (closer, higher zoom) uses `smoothnessIn`; pull-back uses `smoothness`. */
export function zoomSmoothness(
  settings: PlayCameraSettings,
  currentZoom: number,
  targetZoom: number,
): number {
  return targetZoom > currentZoom + LIMIT_EPS
    ? settings.smoothnessIn
    : settings.smoothness;
}

export function computePlayCamera(args: PlayCameraArgs): PlayCameraResult {
  const { hull, area, speed, viewportWidth, viewportHeight, settings } = args;
  const scale = viewportZoomScale(viewportWidth, viewportHeight);
  const gate = clamp(args.lookAheadGate ?? 1, 0, 1);
  const ahead = lookAheadPoint(
    hull,
    area.center,
    lookAheadAmount(speed, settings.lookAhead) * gate,
  );
  const bias = settings.aimBias * clamp(args.aimWeight ?? 1, 0, 1);
  const focus =
    args.aim != null && bias !== 0
      ? lookAheadPoint(ahead, args.aim, bias)
      : ahead;
  const fit = fitZoom(
    hull,
    area,
    viewportWidth,
    viewportHeight,
    settings.paddingM,
    settings.circleFit,
  );
  const rest = args.placing ? settings.placeZoom : settings.restZoom;
  const fromSpeed = speedZoom(
    speed,
    rest,
    settings.fastZoom,
    PLAY_CAMERA_REF_SPEED,
    settings.speedSensitivity,
  );
  const resolved =
    args.placing || speed <= 0
      ? pinRestAutoZoom(fromSpeed, settings, scale)
      : resolveAutoZoom(fit, fromSpeed, settings, scale);

  return {
    lookAhead: focus,
    followOffset: followOffsetFromHull(hull, focus),
    fitZoom: fit,
    speedZoom: fromSpeed,
    autoZoom: resolved.autoZoom,
    appliedZoom: appliedZoom(resolved.autoZoom, args.userZoomFactor),
    limit: resolved.limit,
  };
}
